import { DiagramModel, DefaultNodeModel } from "storm-react-diagrams";
import { CustomNodeModel } from "./CustomNodeModel";

export interface Widget {
  Type: string;
  Output: boolean;
  Disabled: boolean;
  Fields: WidgetField[];
  Header: {
    Color: string;
    BackgroundColor: string;
    Text: string;
  };
  Connections?: WidgetConnections[];
}

export interface WidgetField {
  name: string;
  label: string;
  serializeTo?: string;
  type?: string;
  choices?: string[];
  show?: { when: string; is: string | string[] }[];
  defaultValue?: string;
  description?: string;
  advanced: boolean;
}

export interface WidgetConnections {
  type: "in" | "out";
  label: string;
}

const WIDGETS: {
  [key: string]: Widget;
} = {
  AdaptiveCard: require("./AdaptiveCard.json"),
  AdaptiveCardShow: require("./AdaptiveCardShow.json"),
  API: require("./API.json"),
  Attachment: require("./Attachment.json"),
  CallBot: require("./CallBot.json"),
  CallScenario: require("./CallScenario.json"),
  CallScenarioByIntent: require("./CallScenarioByIntent.json"),
  CallQnA: require("./CallQnA.json"),
  Card: require("./Card.json"),
  Carousel: require("./Carousel.json"),
  CheckVariable: require("./CheckVariable.json"),
  Choice: require("./Choice.json"),
  ChoiceAction: require("./ChoiceAction.json"),
  ChoiceNoButton: require("./ChoiceNoButton.json"),
  ChoiceSuggestion: require("./ChoiceSuggestion.json"),
  CustomCard: require("./CustomCard.json"),
  DateTimeClassifier: require("./DateTimeClassifier.json"),
  DiagnosticTool: require("./DiagnosticTool.json"),
  Event: require("./Event.json"),
  Expression: require("./Expression.json"),
  Login: require("./Login.json"),
  Message: require("./Message.json"),
  MessageEnd: require("./MessageEnd.json"),
  Regex: require("./Regex.json"),
  ResetAllVars: require("./ResetAllVars.json"),
  ResetVars: require("./ResetVars.json"),
  SendEmail: require("./SendEmail.json"),
  SendSMS: require("./SendSMS.json"),
  SetVariables: require("./SetVariables.json"),
  Start: require("./Start.json"),
  SwitchChannel: require("./SwitchChannel.json"),
  Text: require("./Text.json"),
  Telemetry: require("./Telemetry.json")
};

export default WIDGETS;

const DefaultFields: WidgetField[] =
    [
        {
            label: "Telemetry Id",
            name: "telemetryid",
            advanced: true
        },
        {
            label: "Short description",
            name: "description",
            advanced: true,
            type: "multiline"
        }
    ];

const DefaultFieldsForOutputWidget: WidgetField[] =
    [
        {
            label: "Client data",
            name: "clientData",
            advanced: true,
            type: "multiline",
            description: "The value will be sent to the client as additional info",
        },
        {
            label: "Channel data (json)",
            name: "channelData",
            advanced: true,
            type: "multiline",
            description: "The value will be sent to the client in channel data property.",
        }
    ];

export function populateDefaultFields(widget: Widget) {

    if (!widget.Fields) {
        widget.Fields = new Array(); 
    }

    DefaultFields.forEach(f => {
        if (!widget.Fields.find(wf => wf.name === f.name)) {
            widget.Fields.push(f);
        }
    });

    if (widget.Output) {
      DefaultFieldsForOutputWidget.forEach(f => {
        if (!widget.Fields.find(wf => wf.name === f.name)) {
            widget.Fields.push(f);
        }
      });      
    }

    return widget;
}

export function getWidgetColor(type: string) {
  const widget = WIDGETS[type];
  if (widget) {
    return widget.Header.BackgroundColor;
  }
  return "lightgray";
}

export function getWidgetForeColor(type: string) {
  const widget = WIDGETS[type];
  if (widget) {
    return widget.Header.Color;
  }

  return "black";
}

export function createWidget(model: DiagramModel, type: string, id?: string) {
  const widget = WIDGETS[type];
  const newNode = model.addNode(
    new CustomNodeModel(widget.Type, widget.Header.BackgroundColor, id)
  ) as CustomNodeModel;

  if (newNode) {
    if (widget.Connections && widget.Connections.length) {
      widget.Connections.filter(c => c.type === "in").forEach(c => {
        newNode.addInPort(c.label || "In");
      });
      widget.Connections.filter(c => c.type === "out").forEach(c => {
        newNode.addOutPort(c.label || "Out");
      });
    } else {
      newNode.addInPort("In");
      newNode.addOutPort("Out");
    }
    newNode.extras = {
      type
    };

    const getFieldId = (field: WidgetField) => field.serializeTo || field.name;
    widget.Fields
      .filter(field => {
        const fieldId = getFieldId(field);
        return newNode.extras[fieldId] === undefined || newNode.extras[fieldId] === null
      })
      .forEach(field => {
        const fieldId = getFieldId(field);
        let fieldValue = "";

        if (field.type === "yesno") {
          if (field.defaultValue !== undefined) {
            fieldValue = field.defaultValue;
          } else {
            fieldValue = "Yes";
          }
        }

        newNode.extras[fieldId] = fieldValue;
      }
    );
  }

  return newNode;
}

export function changeWidget(node: DefaultNodeModel, newType: string) {
  const widget = WIDGETS[newType];

  if (node) {
    node.name = node.extras.type = newType;
    node.color = widget.Header.BackgroundColor;
    widget.Fields
      .map(field => field.serializeTo || field.name)
      .filter(field => node.extras[field] === undefined || node.extras[field] === null)
      .forEach(field => {
        node.extras[field] = "";
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/designer/widgets/index.ts